import {ReactElement, useEffect} from "react";
import {
    DataGridPremium,
    GridColDef,
    GridRenderCellParams,
    GridValueGetter,
    GridValidRowModel
} from "@mui/x-data-grid-premium";
import {DividendPaymentDateConfig} from "../domain/assetmanager/DividendPaymentDateConfig";
import {
    useCreateDividendPaymentDateDefaultConfiguration,
    useGetDividendPaymentDateConfigurations
} from "../application/DividendConfigService";
import {GetDataApi} from "./types/GetDataApi";
import {Link, useNavigate} from "react-router-dom";
import Grid from "@mui/material/Grid";
import CreateDividendAnyConfigurationDialog from "./CreateDividendAnyConfigurationDialog";
import {CashDividendSource, toHumanReadableFormat} from "../domain/dividend/CashDividendSource";
import {ResponseDataFunction} from "./types/ResponseDataFunction";
import Title from "./Title";
import {useAutoSizeGrid} from "./AutoSizeGridHook";

// Helper type to extract the row type from GridValueGetter for proper typing
type ValueGetterParams<R extends GridValidRowModel> = Parameters<GridValueGetter<R>>;
type ValueGetterRow<R extends GridValidRowModel> = ValueGetterParams<R>[1]; // row is the second parameter

export default function DividendPaymentDateConfigurationsComponent(): ReactElement {
    const navigation = useNavigate();
    const getFunction: GetDataApi<DividendPaymentDateConfig[]> = useGetDividendPaymentDateConfigurations();
    const createFunction: ResponseDataFunction<CashDividendSource, DividendPaymentDateConfig> = useCreateDividendPaymentDateDefaultConfiguration();
    const apiRef = useAutoSizeGrid<DividendPaymentDateConfig>({
        data: getFunction.data,
        loading: getFunction.loading,
        columns: [
            'id',
            'source',
            'isinPrefix',
            'instrumentCurrency',
            'defaultRule',
            'daysAfterExdate'
        ]
    });
    const initialState =
        {
            columns: {
                dimensions: {
                    id: {width: 100},
                    source: {width: 100},
                    isinPrefix: {width: 150},
                    instrumentCurrency: {width: 150},
                    defaultRule: { width: 100},
                    daysAfterExdate: { width: 200}
                }
            }
        }

    useEffect(() => {
        if (createFunction.error) {
            void navigation("dividends/config");
        } else if (createFunction.data.id > 0) {
            void navigation("payment/date/" + createFunction.data.id);
        }
    }, [ createFunction.data, createFunction.error ]);

    const columns: GridColDef<DividendPaymentDateConfig>[] = [
        { field: 'id', headerName: 'ID',
            renderCell: (params: GridRenderCellParams<DividendPaymentDateConfig>) => <Link to={ "payment/date/" + params.row.id.toString() }>{params.row.id}</Link>
        },
        { field: 'source', headerName: 'Dividend Source', valueGetter: (_value, row: ValueGetterRow<DividendPaymentDateConfig>) => { return toHumanReadableFormat(row.source)}},
        { field: 'isinPrefix', headerName: "ISIN Prefix"},
        { field: 'instrumentCurrency', headerName: "Instrument Currency"},
        { field: 'defaultRule', headerName: "Default Rule"},
        { field: 'daysAfterExdate', headerName: "Days After ExDate"},

    ];

    return (
        <>
            <Grid
                container
                spacing={2}
                sx={{
                    alignItems: "center",
                    justifyContent: "space-between"
                }}>
                <Grid size={{ xs: 12, md: 8, lg: 8 }}>
                    <Title>Missing Payment Date Configuration</Title>
                </Grid>
                <Grid size={{ xs: 12, md: 3, lg: 3 }}>
                    <CreateDividendAnyConfigurationDialog
                        title={ "Payment Date"}
                        createFunction={ createFunction }
                        initialSource={ CashDividendSource.DP }
                        urlPath={ "payment/date" }
                        toolTip={ "Rules for estimating paymentDate if missing, based on exDate. Only done if there is a RATE." }
                        usage={ "This creates rules to estimate missing payment dates when they are empty in reports." }
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                    <DataGridPremium
                        apiRef={apiRef}
                        initialState={initialState}
                        loading={getFunction.loading}
                        rows={getFunction.data}
                        columns={columns}
                        density={"compact"}
                        getRowId={(row) => row.id}
                        hideFooter={true}
                        autoHeight={true}
                    />
                </Grid>
            </Grid>
        </>
    );

}